import { useEffect, useRef, useState } from 'react';
import type L from 'leaflet';
import { viewDiffers, type SlideshowBounds } from './deck';
import type { SlideshowSnapshot } from './useSlideshowController';

/**
 * Watches the live map while the presentation panel is open and reports
 * whether the presenter has moved off the area the deck was built from.
 *
 * The decision here (Jason, Sept 3) is update on request, not follow the
 * map: the deck never changes on its own. All this hook does is answer two
 * questions for the panel's offer row — how many observations a fresh deck
 * would hold, and whether that deck would differ from the one on screen.
 *
 * It works only while the panel is up. Fullscreen hides the map, so there
 * is nothing to compare against and no listener attached; closed likewise.
 */

// A pan is a stream of moveend events on a trackpad. Wait for the presenter
// to settle before rebuilding a candidate deck.
const SETTLE_MS = 300;

export interface SlideshowViewWatch {
  /** Photo slides a deck built from the map's current bounds would hold. */
  inViewCount: number;
  /** True when that deck would differ from the presented one. */
  viewMoved: boolean;
}

/** Keeps the previous object when nothing changed, so a stream of moveends
 *  over the same area re-renders the panel once, not once per event. */
function same(previous: SlideshowViewWatch, next: SlideshowViewWatch): SlideshowViewWatch {
  return previous.inViewCount === next.inViewCount && previous.viewMoved === next.viewMoved
    ? previous
    : next;
}

export function useSlideshowViewWatch({
  mapRef,
  active,
  probe,
  presentedBounds,
  presentedPhotoCount,
}: {
  mapRef: React.MutableRefObject<L.Map | null>;
  /** Panel is up and the map is ready. */
  active: boolean;
  /** Same builder the presentation opens with, so the count shown is
   *  exactly the deck Update would produce. */
  probe: () => SlideshowSnapshot | null;
  presentedBounds: SlideshowBounds | null;
  presentedPhotoCount: number;
}): SlideshowViewWatch {
  const [watch, setWatch] = useState<SlideshowViewWatch>({ inViewCount: 0, viewMoved: false });

  const probeRef = useRef(probe);
  probeRef.current = probe;

  useEffect(() => {
    const map = mapRef.current;
    if (!active || !map || !presentedBounds) {
      // Nothing is being watched, so nothing is offered.
      setWatch((w) => same(w, { inViewCount: presentedPhotoCount, viewMoved: false }));
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;

    const evaluate = () => {
      const candidate = probeRef.current();
      if (!candidate) return;
      const inViewCount = candidate.slides.length;
      const viewMoved = viewDiffers(
        { bounds: presentedBounds, photoCount: presentedPhotoCount },
        { bounds: candidate.bounds, photoCount: inViewCount }
      );
      setWatch((w) => same(w, { inViewCount, viewMoved }));
    };

    const onMoveEnd = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(evaluate, SETTLE_MS);
    };

    // Once up front, not only on the next move: the panel can come back
    // from fullscreen after a pan that happened before it, and the offer
    // has to be there when it does.
    evaluate();
    map.on('moveend', onMoveEnd);
    return () => {
      map.off('moveend', onMoveEnd);
      if (timer) clearTimeout(timer);
    };
  }, [mapRef, active, presentedBounds, presentedPhotoCount]);

  return watch;
}
